import { get } from 'lodash';
import React, { useCallback, useState } from 'react';
import { Helmet } from 'react-helmet';

import { TRIP_API } from '../../api/trips';
import FullScreenTripModal from '../../components/Modal/FullScreenTripModal';
import PageBreadcrumbs from '../../components/PageBreadcrumbs';
import TripsTable from '../../components/Table/TripsTable';
import PageContainer from '../../containers/PageContainer';
import { INTERNAL_LINKS } from '../../enum';
import withPageState from '../../hocs/withPageState';
import usePaginatedFiltersQuery from '../../hooks/queries/usePaginatedFiltersQuery';
import useLocationSearchQueryParser from '../../hooks/useLocationSearchQueryParser';
import useModal from '../../hooks/useModal';
import useTableSort from '../../hooks/useTableSort';
import { formatPageTitle } from '../../utils/common';
import { normalizeTripsSchema } from '../../utils/trips';

const SubmissionHistoryTripsView = props => {
  const { t, history, location } = props;
  const queryParams = useLocationSearchQueryParser(location);

  const { rbId, rcId, tripIds = '' } = queryParams;

  const [
    isTripsDetailModalVisible,
    openTripsDetailModalVisible,
    closeTripsDetailModalVisible,
  ] = useModal();
  const [selectedMapConfig, setSelectedMapConfig] = useState({
    routes: [],
    center: [0, 0],
  });

  const tripResponseFormatter = useCallback(response => {
    return {
      data: get(response, 'data', []).map(trp => normalizeTripsSchema(trp, undefined)),
      totalCount: get(response, 'totalCount', 0),
    };
  }, []);

  const { stringTableSort, handleTableSort } = useTableSort({ journeyStartTs: -1 });
  const {
    query: tripsQuery,
    paginationConfig,
    handlePageChange,
    handlePageSizeChange,
  } = usePaginatedFiltersQuery({
    enabled: !!tripIds.split(',').length,
    placeholderData: { data: [], totalCount: 0 },
    queryKey: ['fetchTrips', tripIds, stringTableSort],
    select: tripResponseFormatter,
    queryFn: () =>
      TRIP_API.fetchTrips(
        {
          sort: stringTableSort,
          ids: JSON.stringify(tripIds.split(',').filter(Boolean)),
        },
        paginationConfig.current,
        paginationConfig.pageSize,
      ),
  });

  return (
    <PageContainer
      title={
        <PageBreadcrumbs
          items={[
            {
              label: t(rcId ? 'Receipts' : 'Reimbursements'),
              onClick:
                rcId || rbId
                  ? () =>
                      history.push(
                        `${rcId ? INTERNAL_LINKS.RECEIPTS : INTERNAL_LINKS.REIMBURSEMENT}/${
                          rcId || rbId
                        }`,
                      )
                  : undefined,
            },
            {
              label: t('tripsSubmissionHistory'),
            },
          ]}
        />
      }
    >
      <Helmet>
        <title>{formatPageTitle('Trips Submission History')}</title>
      </Helmet>

      <TripsTable
        t={t}
        asyncSort
        showSearchInput={false}
        loading={tripsQuery.isFetching}
        dataSource={get(tripsQuery.data, 'data', [])}
        onChange={({ current }, filters, sorters) => {
          handlePageChange(current);
          handleTableSort(sorters?.columnKey, sorters?.order);
        }}
        pagination={{
          pageSize: paginationConfig.pageSize,
          total: paginationConfig.total,
          current: paginationConfig.current,
          onShowSizeChange: handlePageSizeChange,
        }}
        handleViewFullScreen={(trp, activeRoutes, mapCenter) => {
          setSelectedMapConfig({
            tripId: trp._id,
            isManual: !trp.gpsVerified || trp.gpsVerified === 'No',
            toLoc: trp.to_loc,
            fromLoc: trp.from_loc,
            routes: activeRoutes,
            center: mapCenter,
          });
          openTripsDetailModalVisible();
        }}
      />

      <FullScreenTripModal
        visible={isTripsDetailModalVisible}
        mapConfig={selectedMapConfig}
        onCancel={() => {
          closeTripsDetailModalVisible();
          setSelectedMapConfig({ center: [0, 0], routes: [] });
        }}
      />
    </PageContainer>
  );
};

export default withPageState(SubmissionHistoryTripsView, true);
